// src/components/avatar/AvatarMaker.js
//
// THE AVATAR MAKER (v2). Students and educators build their avatar from one
// set of drawn parts (avatarParts.js + avatarWear.js), so every avatar in
// the app shares the same style.
//
//   🙂 Face        skin, eyes, eye colour, mouth, cheeks, freckles — free
//   💇 Hair        40 styles (free) + hair colours (natural free, fun ones cost)
//   👕 Clothes     T-shirt & hoodie free; zip hoodie, sweater, polo, jersey,
//                  jacket, varsity cost coins. Blue, gray, white, navy are
//                  free; other colours cost coins (bought once, use on any
//                  clothes).
//   👓 Glasses     20 in 4 levels — the fancier, the more coins
//   🕶️ Sunglasses  20 in 4 levels
//   📍 Pins        butterflies, stars, rockets… on the chest
//   👖 Pants       jeans, khakis, black free; joggers, shorts, cargo,
//                  overalls, plaid, sparkly, rainbow, galaxy… cost coins
//   👟 Shoes       white/black/gray sneakers free; coloured, high-tops,
//                  boots, rain boots, sandals, light-up, gold… cost coins
//                  (pants and shoes only show in the full-body view)
//                  + 32 picture sneakers in 4 levels
//   🧔 Mustache & beard  the drawn mustaches + 31 picture mustaches,
//                  goatees and beards in 4 levels (in the hair colour)
//   Clothes also has 62 picture jackets & shirts in 4 levels.
//   🖼️ Background
//
// Tapping anything tries it on. Things that cost coins show 🔒 + price;
// the bar under the avatar lists what this look still needs and buys each
// one (buyShopItem → server prices from avatarCatalog.json). Save works once
// everything is free or owned. Educators pay the same as students.
// "🛍 Shop" in the header opens the shop (hats, wings, pets, frames).
//
// Props: onClose(), onSaved(look), isEducator, fromShop (opened from the
// shop — then "Shop" just closes this and goes back to it)
//
// demo, startLook — the public home-page demo (containers/preview). Nothing
// is bought or saved to an account: every item can be tried on, the coin
// counter and shop are hidden, and "Use this look" just hands the look back
// through onSaved so the demo page can show it until the visitor leaves.

import ModalPortal from "../common/ModalPortal";
import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import AvatarWithOutfit from "./AvatarWithOutfit";
import { AvatarFace, SKIN_STEPS, shadeForSkin, skinColour, BACKGROUNDS, DEFAULT_LOOK, EYE_COLORS, EYE_NAMES, HAIRSTYLES, HAIR_GROUPS, MOUTH_NAMES, SKINS, cleanLook, randomLook } from "./avatarParts";
import { CATALOG_BY_ID, LEVEL_NAMES, canUse, priceOf, shownItemsOfKind } from "./avatarCatalog";
import { useRewardSettings } from "../../services/rewardSettingsService";
import { useAvatarItemsVersion } from "../../services/avatarItemsService";
import { Coin } from "../coins/CoinCounter";
import { EXCLUSIVE_GRADIENTS } from "./avatarWear";
import CoinCounter from "../coins/CoinCounter";
import { forgetLook, saveAvatarLook, useMyAvatar } from "../../services/avatarService";
import { buyShopItem, useMyCloset } from "../../services/shopService";
import { useWallet } from "../../services/coinService";
import ShopPopup from "../shop/ShopPopup";
import { AvatarViewSwitch, useAvatarView } from "./MyAvatar";
import { SEASONS, nextOpenLabel, onSaleNow, seasonItemsOf, seasonOf, seasonOpen } from "./seasonItems";
import { useSeasonLive } from "./seasons/seasonLive";
import "./avatarMaker.css";

const TABS = [
  { id: "face", label: "Face", icon: "🙂" },
  { id: "hair", label: "Hair", icon: "💇" },
  { id: "clothes", label: "Clothes", icon: "👕" },
  { id: "pants", label: "Pants", icon: "👖" },
  { id: "shoes", label: "Shoes", icon: "👟" },
  { id: "beards", label: "Mustache & beard", icon: "🧔" },
  { id: "glasses", label: "Glasses", icon: "👓" },
  { id: "sunglasses", label: "Sunglasses", icon: "🕶️" },
  { id: "pins", label: "Pins", icon: "📍" },
  { id: "bg", label: "Background", icon: "🖼️" },
];

const PAID_KEYS = ["hairColor", "top", "topColor", "glasses", "pin", "eyesItem", "mouthItem", "mustache", "hairDeco", "bgItem", "pants", "shoes"];
const BODY_TABS = new Set(["pants", "shoes"]); // only seen in full body
// Inside a season, each tab shows only that holiday's things, and a tab is
// only shown when the holiday has something for it (so picking a season
// never leaves you looking at everyday mustaches or pants).
const SEASON_TAB_KINDS = { hair: ["hairdeco"], clothes: ["top"], shoes: ["shoes"], beards: ["mustache"], glasses: ["glasses"], sunglasses: ["sunglasses"], pins: ["pin"], bg: ["bgitem"] };
const seasonHas = (seasonId, tabId) => (SEASON_TAB_KINDS[tabId] || []).some((k) => seasonItemsOf(seasonId, k).length > 0);
const sameLook = (a, b) => JSON.stringify(cleanLook(a)) === JSON.stringify(cleanLook(b));

const swatchBg = (it) => {
  switch (it.pattern) {
    case "rainbow":
      return "linear-gradient(90deg,#ff4d6d,#ff9a3c,#ffd23f,#4cd964,#3a86ff,#8b5cf6)";
    case "tiedye":
      return "radial-gradient(circle at 40% 35%,#ff6fb5,#ffd23f 30%,#4cd9b0 55%,#5aa2ff 75%,#b07cff)";
    case "gold":
      return "linear-gradient(135deg,#fff1a8,#f2c230 40%,#c8901a 65%,#f7d560)";
    case "galaxy":
      return "radial-gradient(circle at 35% 30%,#7b5cff,#3b2a86 55%,#140f36)";
    default:
      return EXCLUSIVE_GRADIENTS[it.pattern] ? `linear-gradient(135deg,${EXCLUSIVE_GRADIENTS[it.pattern].join(",")})` : it.fill;
  }
};

const AvatarMaker = ({ onClose, onSaved, isEducator = false, fromShop = false, demo = false, startLook = null }) => {
  useSeasonLive(); // the admin moved / opened / closed a season → redraw
  const me = useMyAvatar();
  const closet = useMyCloset();
  const wallet = useWallet();
  useRewardSettings(); // keeps admin prices / hidden items current
  useAvatarItemsVersion(); // items uploaded in the Layout Builder
  // everyone pays now (admins are free on the server only); the public demo
  // lets a visitor try everything on, since nothing is kept
  const free = demo;
  const [shop, setShop] = useState(false);
  const [view] = useAvatarView();
  const [season, setSeason] = useState(null); // a Seasons id while inside a season
  const [picking, setPicking] = useState(false); // the season picker is open
  const [look, setLook] = useState(null);
  const [tab, setTab] = useState("face");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null);
  const [confirm, setConfirm] = useState(null); // item id waiting for "yes"
  const [spin, setSpin] = useState(0);

  useEffect(() => {
    if (look !== null) return;
    if (demo) setLook(startLook ? cleanLook(startLook) : { ...DEFAULT_LOOK });
    else if (me.loaded) setLook(me.look ? cleanLook(me.look) : { ...DEFAULT_LOOK });
  }, [demo, startLook, me.loaded, me.look, look]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const owned = closet.owned;
  const usable = (id) => canUse(id, owned, free);

  // paid parts in this look the student doesn't own yet
  const needs = useMemo(() => {
    if (!look || free) return [];
    return PAID_KEYS.map((k) => look[k])
      .filter((id) => id && !usable(id))
      .map((id) => CATALOG_BY_ID[id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [look, owned, free]);

  if (look === null) {
    return (
      <ModalPortal>
        <div className="sbl-am-pop" role="dialog" aria-modal="true">
          <div className="sbl-am sbl-am-loading">Loading…</div>
        </div>
      </ModalPortal>
    );
  }

  const set = (patch) => {
    setMsg(null);
    setConfirm(null);
    setLook((l) => ({ ...l, ...patch }));
  };
  const savedLook = demo ? startLook : me.look;
  const changed = !savedLook || !sameLook(look, savedLook);
  const coins = Number(wallet.coins) || 0;

  const buy = async (it) => {
    if (confirm !== it.id) {
      setConfirm(it.id);
      return;
    }
    setConfirm(null);
    setBusy(it.id);
    setMsg(null);
    try {
      await buyShopItem(it.id);
      setMsg({ tone: "ok", text: `You got ${it.name}! 🎉` });
    } catch (e) {
      setMsg({ tone: "bad", text: e?.message || "Couldn't buy that just now." });
    } finally {
      setBusy("");
    }
  };

  const save = async () => {
    if (demo) {
      onSaved?.(cleanLook(look));
      setMsg({ tone: "ok", text: "Looking great! 🌟 Sign up to keep your avatar — this one disappears when you leave the page." });
      return;
    }
    setBusy("save");
    setMsg(null);
    try {
      const out = await saveAvatarLook(look);
      const uid = getAuth().currentUser?.uid;
      if (uid) forgetLook(uid);
      setMsg({ tone: "ok", text: "Saved! Looking great. 🌟" });
      onSaved?.(out?.look || look);
    } catch (e) {
      setMsg({ tone: "bad", text: e?.message || "Couldn't save just now — try again in a moment." });
      console.warn("[avatar] save failed:", e?.message || e);
    } finally {
      setBusy("");
    }
  };

  // ---- tiles ---------------------------------------------------------------
  const Tag = ({ id }) => {
    if (free) return null;
    const p = priceOf(id);
    if (!p) return <span className="sbl-am-tag is-free">Free</span>;
    if (owned.has(id)) return <span className="sbl-am-tag is-own">✓ Yours</span>;
    if (CATALOG_BY_ID[id]?.exclusive) return <span className="sbl-am-tag is-prize">🏆 Album prize</span>;
    if (!onSaleNow(CATALOG_BY_ID[id])) {
      return (
        <span className="sbl-am-tag is-later" title={nextOpenLabel(CATALOG_BY_ID[id].season)}>
          🗓 <Coin size={12} /> {p}
        </span>
      );
    }
    return (
      <span className="sbl-am-tag is-price">
        🔒 <Coin size={12} /> {p}
      </span>
    );
  };

  const Tile = ({ on, onClick, label, preview, zoom = "", id, full = false }) => (
    <button type="button" className={`sbl-am-tile ${on ? "is-on" : ""} ${id && !usable(id) ? "is-locked" : ""}`} onClick={onClick} aria-pressed={on} title={label}>
      <span className={`sbl-am-tile-face ${zoom}`}>
        <AvatarFace look={preview} full={full} />
      </span>
      <span className="sbl-am-tile-name">{label}</span>
      {id !== undefined && <Tag id={id} />}
    </button>
  );

  const Swatch = ({ it, on, onClick }) => (
    <button type="button" className={`sbl-am-swatch-btn ${on ? "is-on" : ""} ${!usable(it.id) ? "is-locked" : ""}`} onClick={onClick} title={it.name} aria-pressed={on}>
      <span className="sbl-am-swatch-dot" style={{ background: swatchBg(it) }} />
      <span className="sbl-am-swatch-name">{it.name}</span>
      <Tag id={it.id} />
    </button>
  );

  const Section = ({ title, children, swatches }) => (
    <section className="sbl-am-section">
      <h4>{title}</h4>
      <div className={swatches ? "sbl-am-swatches" : "sbl-am-grid"}>{children}</div>
    </section>
  );

  const eyewear = (kind) => {
    const list = shownItemsOfKind(kind, owned, [look.glasses]);
    const levels = [1, 2, 3, 4];
    return (
      <>
        <Section title="">
          <Tile on={!look.glasses} label="None" preview={{ ...look, glasses: "" }} zoom="is-zoom-eyes" onClick={() => set({ glasses: "" })} />
        </Section>
        {levels.map((lv) => (
          <Section key={lv} title={`Level ${lv} — ${LEVEL_NAMES[kind][lv]}`}>
            {list
              .filter((it) => it.level === lv)
              .map((it) => (
                <Tile key={it.id} id={it.id} on={look.glasses === it.id} label={it.name} preview={{ ...look, glasses: it.id }} zoom="is-zoom-eyes" onClick={() => set({ glasses: it.id })} />
              ))}
          </Section>
        ))}
      </>
    );
  };

  // "None" inside a season = back to what you wear every day (your saved
  // clothes / shoes / mustache), not to nothing
  const everyday = (key) => {
    const mine = me.look?.[key];
    if (mine && !CATALOG_BY_ID[mine]?.season) return mine;
    return key === "top" ? DEFAULT_LOOK.top : "";
  };
  const seasonTiles = (kind, key, zoom, noneLabel = "None", full = false) => (
    <Section title="">
      <Tile on={!look[key] || !CATALOG_BY_ID[look[key]]?.season} label={noneLabel} preview={{ ...look, [key]: everyday(key) }} zoom={zoom} full={full} onClick={() => set({ [key]: everyday(key) })} />
      {seasonItemsOf(season, kind).map((it) => (
        <Tile key={it.id} id={it.id} on={look[key] === it.id} label={it.name} preview={{ ...look, [key]: it.id }} zoom={zoom} full={full} onClick={() => set({ [key]: it.id })} />
      ))}
    </Section>
  );

  // picture items (jackets, shoes, beards), in their 4 levels
  const pictureLevels = (kind, key, zoom, full = false) => {
    const list = shownItemsOfKind(kind, owned, [look[key]]).filter((it) => it.img);
    return [1, 2, 3, 4]
      .filter((lv) => list.some((it) => it.level === lv))
      .map((lv) => (
        <Section key={`${kind}${lv}`} title={`Level ${lv} ${"⭐".repeat(lv)} — ${LEVEL_NAMES[kind]?.[lv] || ""}`}>
          {list
            .filter((it) => it.level === lv)
            .map((it) => (
              <Tile key={it.id} id={it.id} on={look[key] === it.id} label={it.name} preview={{ ...look, [key]: it.id }} zoom={zoom} full={full} onClick={() => set({ [key]: it.id })} />
            ))}
        </Section>
      ));
  };

  let panel = null;
  if (picking) {
    panel = (
      <div className="sbl-am-seasons">
        <p className="sbl-am-seasons-lead">Pick a holiday — everything in Hair, Clothes, Glasses, Sunglasses, Pins and Background turns into that holiday's things. Try anything on, any time!</p>
        <div className="sbl-am-seasons-grid">
          {SEASONS.map((x) => (
            <button
              key={x.id}
              type="button"
              className={`sbl-am-season ${seasonOpen(x.id) ? "is-open" : ""}`}
              style={{ "--s1": x.palette.main, "--s2": x.palette.second }}
              onClick={() => {
                setSeason(x.id);
                setPicking(false);
                // stay on this tab only if the season has something for it
                if (!seasonHas(x.id, tab)) setTab("clothes");
              }}
            >
              <span className="sbl-am-season-emoji" aria-hidden="true">
                {x.emoji}
              </span>
              <b>{x.name}</b>
              <small>{seasonOpen(x.id) ? "On sale now!" : nextOpenLabel(x.id)}</small>
            </button>
          ))}
        </div>
      </div>
    );
  } else if (season && tab === "hair") {
    panel = seasonTiles("hairdeco", "hairDeco", "is-zoom-head", "No hair decoration");
  } else if (season && tab === "clothes") {
    panel = seasonTiles("top", "top", "is-zoom-body", "My everyday clothes");
  } else if (season && (tab === "glasses" || tab === "sunglasses")) {
    panel = seasonTiles(tab, "glasses", "is-zoom-eyes");
  } else if (season && tab === "beards") {
    panel = seasonTiles("mustache", "mustache", "is-zoom-face", "My everyday mustache");
  } else if (season && tab === "shoes") {
    panel = seasonTiles("shoes", "shoes", "is-zoom-feet", "My everyday shoes", true);
  } else if (season && !seasonHas(season, tab)) {
    panel = <p className="sbl-am-bodynote">Nothing for this holiday here yet — try another tab.</p>;
  } else if (season && tab === "pins") {
    panel = seasonTiles("pin", "pin", "is-zoom-pin");
  } else if (season && tab === "bg") {
    panel = seasonTiles("bgitem", "bgItem", "", "Plain colour");
  } else if (tab === "face") {
    panel = (
      <>
        <Section title="Skin tone" swatches>
          {SKINS.map((c, i) => (
            <button key={c} type="button" className={`sbl-am-swatch-btn is-round ${look.skin === i && (look.skinShade === undefined || look.skinShade === shadeForSkin(i)) ? "is-on" : ""}`} onClick={() => set({ skin: i, skinShade: shadeForSkin(i) })} aria-label={`Skin tone ${i + 1}`} aria-pressed={look.skin === i}>
              <span className="sbl-am-swatch-dot" style={{ background: c }} />
            </button>
          ))}
        </Section>
        <div className="sbl-am-skinslider">
          <span aria-hidden="true">☀️ Lighter</span>
          <input
            type="range"
            min="0"
            max={SKIN_STEPS}
            step="1"
            value={Number.isFinite(look.skinShade) ? look.skinShade : shadeForSkin(look.skin)}
            onChange={(e) => {
              const v = Number(e.target.value);
              set({ skinShade: v, skin: Math.round((v / SKIN_STEPS) * 9) });
            }}
            aria-label="Make my skin lighter or darker"
            style={{ "--sbl-skin": skinColour(look) }}
          />
          <span aria-hidden="true">Darker 🌙</span>
        </div>
        <Section title="Eyes">
          {EYE_NAMES.map((n, i) => (
            <Tile key={n} on={look.eyes === i} label={n} preview={{ ...look, eyes: i }} zoom="is-zoom-eyes" onClick={() => set({ eyes: i })} />
          ))}
        </Section>
        <Section title="Eye colour" swatches>
          {EYE_COLORS.map((c, i) => (
            <button key={c} type="button" className={`sbl-am-swatch-btn is-round ${look.eyeColor === i ? "is-on" : ""}`} onClick={() => set({ eyeColor: i })} aria-label={`Eye colour ${i + 1}`} aria-pressed={look.eyeColor === i}>
              <span className="sbl-am-swatch-dot" style={{ background: c }} />
            </button>
          ))}
        </Section>
        <Section title="Mouth">
          {MOUTH_NAMES.map((n, i) => (
            <Tile key={n} on={look.mouth === i} label={n} preview={{ ...look, mouth: i }} zoom="is-zoom-mouth" onClick={() => set({ mouth: i })} />
          ))}
        </Section>
        <Section title="Special eyes ✨">
          <Tile on={!look.eyesItem} label="Regular" preview={{ ...look, eyesItem: "" }} zoom="is-zoom-eyes" onClick={() => set({ eyesItem: "" })} />
          {shownItemsOfKind("eyes", owned, [look.eyesItem]).map((it) => (
            <Tile key={it.id} id={it.id} on={look.eyesItem === it.id} label={it.name} preview={{ ...look, eyesItem: it.id }} zoom="is-zoom-eyes" onClick={() => set({ eyesItem: it.id })} />
          ))}
        </Section>
        <Section title="Special mouths 😜">
          <Tile on={!look.mouthItem} label="Regular" preview={{ ...look, mouthItem: "" }} zoom="is-zoom-mouth" onClick={() => set({ mouthItem: "" })} />
          {shownItemsOfKind("mouth", owned, [look.mouthItem]).map((it) => (
            <Tile key={it.id} id={it.id} on={look.mouthItem === it.id} label={it.name} preview={{ ...look, mouthItem: it.id }} zoom="is-zoom-mouth" onClick={() => set({ mouthItem: it.id })} />
          ))}
        </Section>
        <Section title="Extras">
          <Tile on={look.blush} label={look.blush ? "Rosy cheeks ✓" : "Rosy cheeks"} preview={{ ...look, blush: true }} zoom="is-zoom-face" onClick={() => set({ blush: !look.blush })} />
          <Tile on={look.freckles} label={look.freckles ? "Freckles ✓" : "Freckles"} preview={{ ...look, freckles: true }} zoom="is-zoom-face" onClick={() => set({ freckles: !look.freckles })} />
        </Section>
      </>
    );
  } else if (tab === "hair") {
    panel = (
      <>
        <Section title="Hair colour" swatches>
          {shownItemsOfKind("haircolor", owned, [look.hairColor]).map((it) => (
            <Swatch key={it.id} it={it} on={look.hairColor === it.id} onClick={() => set({ hairColor: it.id })} />
          ))}
        </Section>
        {HAIR_GROUPS.map(([g, title]) => (
          <Section key={g} title={title}>
            {HAIRSTYLES.filter((h) => h[2] === g).map(([id, n]) => (
              <Tile key={id} on={look.hair === id} label={n} preview={{ ...look, hair: id, glasses: "" }} zoom="is-zoom-head" onClick={() => set({ hair: id })} />
            ))}
          </Section>
        ))}
      </>
    );
  } else if (tab === "clothes") {
    panel = (
      <>
        <Section title="Colour" swatches>
          {shownItemsOfKind("color", owned, [look.topColor]).map((it) => (
            <Swatch key={it.id} it={it} on={look.topColor === it.id} onClick={() => set({ topColor: it.id })} />
          ))}
        </Section>
        <Section title="Clothes (any colour above)">
          {shownItemsOfKind("top", owned, [look.top])
            .filter((it) => !it.img)
            .map((it) => (
              <Tile key={it.id} id={it.id} on={look.top === it.id} label={it.name} preview={{ ...look, top: it.id }} zoom="is-zoom-body" onClick={() => set({ top: it.id })} />
            ))}
        </Section>
        <h3 className="sbl-am-group">🧥 Jackets &amp; Shirts</h3>
        {pictureLevels("top", "top", "is-zoom-body")}
      </>
    );
  } else if (tab === "pants" || tab === "shoes") {
    const key = tab;
    const free0 = shownItemsOfKind(key, owned, [look[key]]);
    panel = (
      <>
        <p className="sbl-am-bodynote">{key === "pants" ? "👖 Pants" : "👟 Shoes"} show on your full-body avatar.</p>
        <Section title={key === "pants" ? "Pants & shorts" : "Shoes & sneakers"}>
          <Tile on={!look[key]} label={key === "pants" ? "Everyday blue" : "Everyday white"} preview={{ ...look, [key]: "" }} zoom={`is-zoom-${key === "pants" ? "legs" : "feet"}`} full onClick={() => set({ [key]: "" })} />
          {free0
            .filter((it) => !it.img)
            .map((it) => (
              <Tile key={it.id} id={it.id} on={look[key] === it.id} label={it.name} preview={{ ...look, [key]: it.id }} zoom={`is-zoom-${key === "pants" ? "legs" : "feet"}`} full onClick={() => set({ [key]: it.id })} />
            ))}
        </Section>
        {key === "shoes" && (
          <>
            <h3 className="sbl-am-group">👟 Sneakers</h3>
            {pictureLevels("shoes", "shoes", "is-zoom-feet", true)}
          </>
        )}
      </>
    );
  } else if (tab === "beards") {
    panel = (
      <>
        <Section title="Mustaches 🥸">
          <Tile on={!look.mustache} label="None" preview={{ ...look, mustache: "" }} zoom="is-zoom-mouth" onClick={() => set({ mustache: "" })} />
          {shownItemsOfKind("mustache", owned, [look.mustache])
            .filter((it) => !it.img)
            .map((it) => (
              <Tile key={it.id} id={it.id} on={look.mustache === it.id} label={it.name} preview={{ ...look, mustache: it.id }} zoom="is-zoom-mouth" onClick={() => set({ mustache: it.id })} />
            ))}
        </Section>
        <p className="sbl-am-bodynote">🧔 Mustaches and beards come in your hair colour.</p>
        {pictureLevels("mustache", "mustache", "is-zoom-face")}
      </>
    );
  } else if (tab === "glasses" || tab === "sunglasses") {
    panel = eyewear(tab);
  } else if (tab === "pins") {
    panel = (
      <Section title="Pick a pin for your shirt">
        <Tile on={!look.pin} label="None" preview={{ ...look, pin: "" }} zoom="is-zoom-pin" onClick={() => set({ pin: "" })} />
        {shownItemsOfKind("pin", owned, [look.pin]).map((it) => (
          <Tile key={it.id} id={it.id} on={look.pin === it.id} label={it.name} preview={{ ...look, pin: it.id }} zoom="is-zoom-pin" onClick={() => set({ pin: it.id })} />
        ))}
      </Section>
    );
  } else {
    panel = (
      <Section title="Background" swatches>
        {BACKGROUNDS.map((c, i) => (
          <button key={c} type="button" className={`sbl-am-swatch-btn is-round ${look.bg === i ? "is-on" : ""}`} onClick={() => set({ bg: i, bgItem: "" })} aria-label={`Background ${i + 1}`} aria-pressed={look.bg === i}>
            <span className="sbl-am-swatch-dot is-big" style={{ background: c }} />
          </button>
        ))}
      </Section>
    );
  }

  return (
    <ModalPortal>
      <div className="sbl-am-pop" role="dialog" aria-modal="true" aria-label="Make your avatar" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
        <div className="sbl-am">
          <header className="sbl-am-head">
            <h2>🎨 {isEducator ? "Your avatar" : "Make your avatar"}</h2>
            {demo ? (
              <span className="sbl-am-demo-tag">✨ Free demo — try anything on!</span>
            ) : (
              <>
                <CoinCounter />
                <button type="button" className="sbl-am-shop" onClick={() => (fromShop ? onClose?.() : setShop(true))}>
                  <span aria-hidden="true">🛍</span> Shop
                </button>
              </>
            )}
            <button type="button" className="sbl-am-x" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </header>

          <div className="sbl-am-body">
            <aside className="sbl-am-stage">
              <AvatarViewSwitch />
              <div className={`sbl-am-big ${spin ? "is-pop" : ""}`} key={spin}>
                <AvatarWithOutfit look={look} outfit={closet.outfit} size={view === "full" || BODY_TABS.has(tab) ? 290 : 190} full={view === "full" || BODY_TABS.has(tab)} />
              </div>

              {needs.length > 0 && (
                <div className="sbl-am-needs">
                  <p>To keep this look, get:</p>
                  {needs.map((it) => (
                    <div key={it.id} className="sbl-am-need">
                      <span className="sbl-am-need-name">{it.name}</span>
                      {it.exclusive ? (
                        <span className="sbl-am-prize-note">Finish a sticker album 🏆</span>
                      ) : !onSaleNow(it) ? (
                        <span className="sbl-am-prize-note">🗓 {nextOpenLabel(it.season)}</span>
                      ) : (
                      <button type="button" className={`sbl-am-buy ${confirm === it.id ? "is-confirm" : ""}`} disabled={!!busy || coins < priceOf(it.id)} onClick={() => buy(it)}>
                        {busy === it.id ? <span className="sbl-spinner is-small" aria-label="Loading" /> : coins < priceOf(it.id) ? `Need ${priceOf(it.id) - coins} more` : confirm === it.id ? `Yes, buy for ${priceOf(it.id)}` : (
                          <>
                            Buy <Coin size={13} /> {priceOf(it.id)}
                          </>
                        )}
                      </button>
                      )}
                    </div>
                  ))}
                  {needs.some((it) => !it.exclusive && coins < priceOf(it.id)) && <p className="sbl-am-small">Earn more coins in games, songs and Treasure Quest! 🪙</p>}
                </div>
              )}

              <button
                type="button"
                className="sbl-am-dice"
                onClick={() => {
                  set(randomLook(owned, free));
                  setSpin((n) => n + 1);
                }}
              >
                🎲 Surprise me
              </button>
              <button type="button" className="sbl-am-save" disabled={!!busy || !changed || needs.length > 0} onClick={save}>
                {busy === "save" ? <><span className="sbl-spinner is-small" aria-label="Saving" /> Saving…</> : demo ? (changed ? "Use this look ✓" : "Wearing it ✓") : !changed ? "Saved ✓" : needs.length ? "Get the 🔒 items to save" : "Save my avatar ✓"}
              </button>
              {msg && <p className={`sbl-am-msg is-${msg.tone}`}>{msg.text}</p>}
              {demo && !msg && <p className="sbl-am-note">In the portal, students earn coins to unlock clothes, glasses and more. Nothing here is saved.</p>}
              {!demo && !me.look && me.avatarUrl && <p className="sbl-am-note">Your new avatar replaces your old picture everywhere once you save.</p>}
            </aside>

            <section className="sbl-am-parts">
              <nav className="sbl-am-tabs" role="tablist">
                <button type="button" className={`sbl-am-seasons-btn ${picking || season ? "is-on" : ""}`} onClick={() => setPicking((v) => !v)} aria-pressed={picking}>
                  <span aria-hidden="true">🎉</span> Seasons
                </button>
                {TABS.filter((t) => !season || seasonHas(season, t.id)).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={!picking && tab === t.id}
                    className={!picking && tab === t.id ? "is-on" : ""}
                    onClick={() => {
                      setPicking(false);
                      setTab(t.id);
                    }}
                  >
                    <span aria-hidden="true">{t.icon}</span> {t.label}
                  </button>
                ))}
              </nav>
              {season && !picking && (
                <div className="sbl-am-season-bar" style={{ "--s1": seasonOf(season)?.palette.main, "--s2": seasonOf(season)?.palette.second }}>
                  <span>
                    {seasonOf(season)?.emoji} <b>{seasonOf(season)?.name}</b> · {seasonOpen(season) ? "on sale now!" : `try it on now — ${nextOpenLabel(season).replace(/^On /, "on ")}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setSeason(null);
                      setPicking(false);
                    }}
                  >
                    ✕ Leave season
                  </button>
                </div>
              )}
              <div className="sbl-am-panel">{panel}</div>
            </section>
          </div>
        </div>
      </div>
      {shop && !demo && (
        <div onMouseDown={(e) => e.stopPropagation()}>
          <ShopPopup educator={isEducator} fromMaker onClose={() => setShop(false)} />
        </div>
      )}
    </ModalPortal>
  );
};

export default AvatarMaker;
